import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Box,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  TreeDeciduous,
  Home,
  Gift,
  Watch,
  Award,
  Ruler,
  Palette,
  Gem
} from 'lucide-react';
import { toast } from 'sonner';

// Wood types
const WOOD_TYPES = [
  { value: 'noce', label: 'Legno di noce', icon: TreeDeciduous },
  { value: 'ciliegio', label: 'Legno di ciliegio', icon: TreeDeciduous },
  { value: 'acero', label: 'Legno di acero', icon: TreeDeciduous },
  { value: 'mogano', label: 'Mogano', icon: TreeDeciduous },
  { value: 'ebano', label: 'Ebano', icon: TreeDeciduous },
  { value: 'palissandro', label: 'Palissandro', icon: TreeDeciduous },
  { value: 'ulivo', label: 'Legno di ulivo', icon: TreeDeciduous },
  { value: 'radica', label: 'Radica (vari)', icon: Sparkles },
  { value: 'intarsio', label: 'Intarsio multi-legno', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Box types
const BOX_TYPES = [
  { value: 'portagioie', label: 'Portagioie', icon: Gem },
  { value: 'portaorologi', label: 'Portaorologi', icon: Watch },
  { value: 'portasigari', label: 'Humidor / Portasigari', icon: Box },
  { value: 'portapenne', label: 'Portapenne / Scrittoio', icon: Award },
  { value: 'portafoto', label: 'Cofanetto portafoto', icon: Heart },
  { value: 'ricordi', label: 'Scatola dei ricordi', icon: Heart },
  { value: 'gioco', label: 'Scatola per giochi (carte, scacchi)', icon: Box },
  { value: 'cucito', label: 'Scatola da cucito', icon: Box },
  { value: 'te', label: 'Scatola porta tè', icon: Box },
  { value: 'altro', label: 'Altro cofanetto', icon: Box },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'piccolo', label: 'Piccolo (fino a 15 cm)', icon: Ruler },
  { value: 'medio', label: 'Medio (15-25 cm)', icon: Ruler },
  { value: 'grande', label: 'Grande (25-40 cm)', icon: Ruler },
  { value: 'xl', label: 'Extra Large (oltre 40 cm)', icon: Ruler },
  { value: 'personalizzato', label: 'Misure personalizzate', icon: Sparkles },
];

// Finish options
const FINISH_OPTIONS = [
  { value: 'lucida', label: 'Lucidatura a specchio', icon: Star },
  { value: 'satinata', label: 'Satinata / Semi-opaca', icon: Sparkles },
  { value: 'naturale', label: 'Naturale (olio/cera)', icon: TreeDeciduous },
  { value: 'intarsiata', label: 'Con intarsio decorativo', icon: Palette },
  { value: 'madreperlata', label: 'Con inserti madreperla', icon: Gem },
  { value: 'metallo', label: 'Con finiture in metallo', icon: Award },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Internal lining
const LINING_OPTIONS = [
  { value: 'velluto', label: 'Velluto', icon: Heart },
  { value: 'seta', label: 'Seta', icon: Sparkles },
  { value: 'pelle', label: 'Pelle / Alcantara', icon: Star },
  { value: 'feltro', label: 'Feltro', icon: Heart },
  { value: 'legno', label: 'Legno a vista', icon: TreeDeciduous },
  { value: 'personalizzato', label: 'Altro (specifica)', icon: Sparkles },
];

// Purpose/Occasion
const PURPOSE_OPTIONS = [
  { value: 'uso_personale', label: 'Uso personale', icon: Home },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'matrimonio', label: 'Regalo matrimonio', icon: Heart },
  { value: 'anniversario', label: 'Anniversario', icon: Heart },
  { value: 'commemorativo', label: 'Ricordo speciale', icon: Star },
  { value: 'collezione', label: 'Per collezione', icon: Gem },
  { value: 'professionale', label: 'Uso professionale', icon: Award },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '50-100', label: '€50 - €100', description: 'Scatola semplice' },
  { value: '100-200', label: '€100 - €200', description: 'Legno pregiato' },
  { value: '200-400', label: '€200 - €400', description: 'Con scomparti' },
  { value: '400-700', label: '€400 - €700', description: 'Intarsiato / Lusso' },
  { value: '700-1200', label: '€700 - €1.200', description: 'Pezzo da collezione' },
  { value: '1200+', label: '€1.200+', description: 'Opera d\'arte' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Urgente' },
  { value: '3weeks', label: '3 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Opera complessa' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example boxes
const BOX_EXAMPLES = [
  {
    idea: "Portagioie per anello di fidanzamento",
    result: "Cofanetto in ebano e acero 8x8cm, coperchio con intarsio a stella, interno in velluto bordeaux, cerniera nascosta",
    image: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400&h=300&fit=crop",
    category: "Portagioie"
  },
  {
    idea: "Scatola dei ricordi per nascita figlio",
    result: "Cofanetto in ciliegio 30x20cm con nome inciso, scomparti per braccialetto nascita, ciuffo capelli, prima foto",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
    category: "Ricordi"
  },
  {
    idea: "Portaorologi per collezione 12 pezzi",
    result: "Cofanetto in noce con vetro, 12 cuscini in pelle, chiusura con chiave, fondo in alcantara blu",
    image: "https://images.unsplash.com/photo-1509048191080-d2984bad6ae5?w=400&h=300&fit=crop",
    category: "Portaorologi"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho commissionato un portagioie per la proposta di matrimonio. Il cofanetto in ebano con il nostro nome intarsiato ha reso il momento ancora più speciale. Lei l'ha adorato!",
    author: "Marco V.",
    location: "Roma"
  },
  {
    text: "Cercavo una scatola dei ricordi per conservare i cimeli di mia nonna. L'artigiano ha creato un pezzo bellissimo in noce con scomparti su misura. È diventato un tesoro di famiglia.",
    author: "Giovanna L.",
    location: "Napoli"
  },
  {
    text: "Il mio portaorologi in radica è un'opera d'arte. Ogni volta che lo apro per scegliere l'orologio del giorno, mi emoziono. Qualità e bellezza uniche.",
    author: "Roberto S.",
    location: "Milano"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Quale legno è migliore per un cofanetto?",
    answer: "Il noce e il ciliegio sono classici ed eleganti. Per il lusso, mogano, ebano o palissandro. La radica offre venature uniche. Per intarsi, si combinano legni di colori diversi (acero chiaro con ebano scuro)."
  },
  {
    question: "Posso personalizzare l'interno del cofanetto?",
    answer: "Assolutamente! L'interno può avere scomparti su misura, cuscini per orologi, anelli, divisori rimovibili. Il rivestimento può essere in velluto, seta, pelle o alcantara nel colore preferito."
  },
  {
    question: "È possibile aggiungere incisioni o intarsi?",
    answer: "Sì, sono possibili incisioni laser o a mano (nomi, date, frasi), intarsi con legni contrastanti, inserti in madreperla o metalli preziosi. L'artigiano proporrà la tecnica più adatta."
  },
  {
    question: "Quanto tempo ci vuole per un cofanetto su misura?",
    answer: "Un cofanetto semplice richiede 2-3 settimane. Per intarsi elaborati o finiture speciali, calcola 1-2 mesi. Humidor e pezzi complessi possono richiedere più tempo."
  },
  {
    question: "L'humidor mantiene correttamente l'umidità?",
    answer: "Gli humidor artigianali sono costruiti con legno di cedro spagnolo interno che regola naturalmente l'umidità. Includono igrometro e umidificatore per una conservazione perfetta dei sigari."
  }
];

export default function CofanettiScatoleRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    wood_type: '',
    box_type: '',
    size: '',
    finish: '',
    lining: '',
    purpose: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Cofanetti e Scatole Su Misura | Falegnameria Artigianale | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `box-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi il cofanetto che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_box_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const woodLabel = WOOD_TYPES.find(t => t.value === formData.wood_type)?.label || '';
      const boxLabel = BOX_TYPES.find(t => t.value === formData.box_type)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(t => t.value === formData.size)?.label || '';
      const finishLabel = FINISH_OPTIONS.find(t => t.value === formData.finish)?.label || '';
      const liningLabel = LINING_OPTIONS.find(t => t.value === formData.lining)?.label || '';
      const purposeLabel = PURPOSE_OPTIONS.find(p => p.value === formData.purpose)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (boxLabel) details.push(`📦  Tipo: ${boxLabel}`);
      if (woodLabel) details.push(`🌳  Legno: ${woodLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (finishLabel) details.push(`✨  Finitura: ${finishLabel}`);
      if (liningLabel) details.push(`🎀  Interno: ${liningLabel}`);
      if (purposeLabel) details.push(`🎯  Utilizzo: ${purposeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${boxLabel} in ${woodLabel}`.trim() || 'Cofanetto su misura',
        description: fullDescription,
        category: 'Cofanetti e Scatole',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai falegnami.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('box-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Cofanetti e Scatole Su Misura | Falegnameria Artigianale Italiana | Rarifex</title>
        <meta name="description" content="Cofanetti e scatole in legno su misura: portagioie, portaorologi, humidor realizzati da maestri falegnami italiani. Artigianato Made in Italy. Gratuito e senza impegno." />
        <meta name="keywords" content="cofanetto legno, portagioie legno, portaorologi legno, humidor artigianale, scatola legno, scatola ricordi, cofanetto personalizzato" />
        <link rel="canonical" href="https://www.rarifex.com/cofanetti-scatole-su-misura" />
        <meta property="og:title" content="Cofanetti e Scatole Su Misura | Falegnameria Artigianale Italiana | Rarifex" />
        <meta property="og:description" content="Cofanetti e scatole in legno su misura realizzati da maestri falegnami italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/cofanetti-scatole-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-violet-50/50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Box className="w-4 h-4" />
                    Cofanetti Artigianali
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il cofanetto perfetto{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-amber-600">
                      per i tuoi tesori
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri falegnami creano scrigni unici per i tuoi oggetti preziosi.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Portagioie, portaorologi, scatole dei ricordi: ogni dettaglio su misura.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="box-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Portagioie per proposta di matrimonio"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="box-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi il cofanetto che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Cosa deve contenere, stile, scomparti necessari. Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="box-description-textarea"
                        />
                      </div>
                      
                      {/* Box Type & Wood Type */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo</Label>
                          <select
                            value={formData.box_type}
                            onChange={(e) => handleInputChange('box_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="box-type-select"
                          >
                            <option value="">Seleziona tipo...</option>
                            {BOX_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Legno</Label>
                          <select
                            value={formData.wood_type}
                            onChange={(e) => handleInputChange('wood_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="box-wood-select"
                          >
                            <option value="">Seleziona legno...</option>
                            {WOOD_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Size & Finish */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="box-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Finitura</Label>
                          <select
                            value={formData.finish}
                            onChange={(e) => handleInputChange('finish', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="box-finish-select"
                          >
                            <option value="">Seleziona...</option>
                            {FINISH_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Lining & Purpose */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Interno</Label>
                          <select
                            value={formData.lining}
                            onChange={(e) => handleInputChange('lining', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="box-lining-select"
                          >
                            <option value="">Seleziona...</option>
                            {LINING_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Utilizzo</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="box-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PURPOSE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="box-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="box-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-violet-400 hover:bg-violet-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica immagini (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Riferimento ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-violet-600 to-amber-600 hover:from-violet-700 hover:to-amber-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="box-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da falegnami verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai il tuo cofanetto artigianale
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci il cofanetto che desideri' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da falegnami esperti' },
                { icon: Star, title: 'Scegli', desc: 'L\'artigiano e il progetto ideale' },
                { icon: Heart, title: 'Ammira', desc: 'Il tuo cofanetto prende vita' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-100 to-amber-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-violet-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-gradient-to-b from-violet-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cofanetti Realizzati
              </h2>
              <p className="text-lg text-slate-600">
                Dalla richiesta al cofanetto finito
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {BOX_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-violet-100 text-violet-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.location}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gradient-to-b from-white to-violet-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-violet-600 to-amber-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto a creare il tuo cofanetto?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Richiedi preventivi gratuiti dai migliori falegnami italiani
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-violet-600 hover:bg-violet-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="box-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dai falegnami, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                  onClick={() => navigate('/login?redirect=/cofanetti-scatole-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
